import {useEffect,useRef,useState} from 'react';
import type {Snapshot} from '@platform/contracts/x';
import {createGameMusic} from './gameAudio';

// Current server phase controls volume; viewing an upcoming sale or historical
// result never changes the current game's music. Keep the same loop across phases.
export function useGameMusic(state:Snapshot|null,previous:Snapshot|null,sound:boolean,ready:boolean,tracks:readonly string[],salesPercent:number,livePercent:number){
 // Pick once per opening; sound toggles, snapshots and draw rollover keep it.
 const [choice]=useState(Math.random),sources=[...new Set(tracks.filter(Boolean))];
 const source=sources[Math.floor(choice*sources.length)]??'';
 const [visible,setVisible]=useState(!document.hidden),music=useRef<ReturnType<typeof createGameMusic>|null>(null);
 const enabled=!!state&&sound&&ready&&visible&&!state.isUpcoming&&!state.x.paused&&(salesPercent>0||livePercent>0);
 const previousNoticeEnd=previous?.awardPause?.popupEndsAtUtc;
 useEffect(()=>{const change=()=>setVisible(!document.hidden);document.addEventListener('visibilitychange',change);return()=>document.removeEventListener('visibilitychange',change);},[]);
 useEffect(()=>{
  if(!enabled||!source)return;
  const lease=createGameMusic(source);music.current=lease;void lease.play().catch(()=>{});
  return()=>{lease.pause();if(music.current===lease)music.current=null;};
 },[enabled,source]);
 useEffect(()=>{
  if(!enabled||!state)return;
  const offset=Date.now()-Date.parse(state.serverTimeUtc),now=()=>Date.now()-offset;
  const close=state.salesCloseAtUtc?Date.parse(state.salesCloseAtUtc):Infinity;
  const priorEnd=previousNoticeEnd?Date.parse(previousNoticeEnd):0;
  const level=(value:number)=>Number.isFinite(value)?Math.min(100,Math.max(0,value))/100:0;
  // Countdown shares the configured quiet live level. A delayed Preparing
  // snapshot still lowers the existing loop at sale close, without stopping it.
  const quiet=level(livePercent);
  const update=()=>music.current?.setVolume(state.status==='SalesOpen'&&now()>=priorEnd&&now()<close?level(salesPercent):quiet);
  update();const timers:number[]=[];
  if(state.status==='SalesOpen')for(const at of [priorEnd,close])if(Number.isFinite(at)&&at>now())timers.push(window.setTimeout(update,at-now()));
  return()=>timers.forEach(clearTimeout);
 },[enabled,source,state?.drawId,state?.status,state?.salesCloseAtUtc,previousNoticeEnd,salesPercent,livePercent]);
}
